const API_BASE_URL = (
  process.env.REACT_APP_ADMIN_API_BASE_URL ||
  "http://secondbite-manage.localhost/admin-api/v1"
).replace(/\/+$/, "");

const TOKEN_KEY = "secondbite_admin_token";
const USER_KEY = "secondbite_admin_user";

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const getErrorMessage = (payload, fallback) => {
  const validationMessage = payload?.errors
    ? Object.values(payload.errors).flat().find(Boolean)
    : null;

  return validationMessage || payload?.message || fallback;
};

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY);

const storeAdminUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  window.dispatchEvent(new CustomEvent("admin-permissions-changed", {
    detail: { permissions: Array.isArray(user?.permissions) ? user.permissions : [] },
  }));
  return user;
};

const fetchAdminPermissions = async (adminId, token) => {
  if (!adminId || !token) {
    return null;
  }

  let response;

  try {
    response = await fetch(`${API_BASE_URL}/administrators/${adminId}`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    return null;
  }

  const payload = await readJson(response);
  if (!response.ok || !payload?.success) {
    return null;
  }

  return Array.isArray(payload.data?.permissions) ? payload.data.permissions : null;
};

const mergeAdminProfile = async (profile, previous = getStoredAdmin()) => {
  if (!profile) {
    return previous;
  }

  let permissions = profile.permissions;

  if (!Array.isArray(permissions) && Array.isArray(previous?.permissions)) {
    permissions = previous.permissions;
  }

  if (!Array.isArray(permissions) && profile.id) {
    permissions = await fetchAdminPermissions(profile.id, getAdminToken());
  }

  if (!Array.isArray(permissions)) {
    permissions = [];
  }

  return { ...profile, permissions };
};

export const getStoredAdmin = () => {
  const storedUser = localStorage.getItem(USER_KEY);

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    localStorage.removeItem(USER_KEY);
    return null;
  }
};

export const isAdminAuthenticated = () => Boolean(getAdminToken());

export const isSuperAdmin = (admin = getStoredAdmin()) => {
  if (!admin) {
    return false;
  }

  const permissions = admin.permissions;
  return Array.isArray(permissions) && permissions.length === 0;
};

export const hasAdminPermission = (permission, admin = getStoredAdmin()) => {
  if (!permission) {
    return true;
  }

  if (!admin) {
    return false;
  }

  if (isSuperAdmin(admin)) {
    return true;
  }

  return Array.isArray(admin.permissions) && admin.permissions.includes(permission);
};

export const updateStoredAdmin = (updates) => {
  const admin = getStoredAdmin();
  if (!admin) {
    return null;
  }

  const nextAdmin = { ...admin, ...updates };
  if (!Array.isArray(nextAdmin.permissions)) {
    nextAdmin.permissions = Array.isArray(admin.permissions) ? admin.permissions : [];
  }
  return storeAdminUser(nextAdmin);
};

export const refreshAdminProfile = async () => {
  const token = getAdminToken();
  if (!token) {
    return null;
  }

  let response;

  try {
    response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    return null;
  }

  const payload = await readJson(response);

  if (response.status === 401) {
    clearAdminSession();
    window.location.replace("/login");
    return null;
  }

  if (!response.ok || !payload?.success || !payload?.data) {
    return null;
  }

  const mergedAdmin = await mergeAdminProfile(payload.data);
  return storeAdminUser(mergedAdmin);
};

export const clearAdminSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem("token");
};

export const loginAdmin = async (email, password) => {
  let response;

  try {
    response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });
  } catch {
    throw new Error("Unable to connect to the SecondBite backend.");
  }

  const payload = await readJson(response);

  if (!response.ok || !payload?.success || !payload?.data?.token) {
    throw new Error(getErrorMessage(payload, "Login failed. Please try again."));
  }

  localStorage.setItem(TOKEN_KEY, payload.data.token);
  const mergedAdmin = await mergeAdminProfile(payload.data.user);
  storeAdminUser(mergedAdmin);
  localStorage.removeItem("token");

  return mergedAdmin;
};

export const logoutAdmin = async () => {
  const token = getAdminToken();

  try {
    if (token) {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    }
  } finally {
    clearAdminSession();
  }
};
